import React, {
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect
} from 'react';
import { useSelector } from 'react-redux';
import { FullHeaderLeftSideFullPanelLayout } from 'layouts/fullHeader.LeftSideFullPanel.layout';
import { currentUser } from 'redux/session/selectors';
import {
  Loading,
  InlineNotification,
  Button,
  Dropdown,
  DatePicker,
  DatePickerInput,
  MultiSelect,
  TextInput,
  NumberInput
} from 'carbon-components-react';
import { Report20 } from '@carbon/icons-react';
import {
  useReports,
  useReportConfig,
  useFilterState,
  useFilterOptions,
  useGenerateReport,
  createTokenAuthProvider
} from 'lib/reporting-core-sdk/react.js';
import {
  createReportingClient,
  resolveApiUrl
} from 'lib/reporting-core-sdk/index.js';
import { formatDateForApi, parseDateString } from './utils/formatters';
import { downloadFile } from 'utilities/helper.util';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import './index.scss';

dayjs.extend(utc);
dayjs.extend(timezone);

// Helper function to download a file with authentication
async function downloadWithAuth(
  url: string,
  token: string,
  defaultFilename: string,
  mimeType: string
): Promise<void> {
  const response = await fetch(url, {
    headers: {
      Authorization: `Bearer ${token}`
    }
  });

  if (!response.ok) {
    throw new Error(
      `Download failed: ${response.status} ${response.statusText}`
    );
  }

  const blob = await response.blob();

  // Try to get filename from Content-Disposition header
  const contentDisposition = response.headers.get('Content-Disposition');
  let filename = defaultFilename;
  if (contentDisposition) {
    const filenameMatch = contentDisposition.match(
      /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
    );
    if (filenameMatch && filenameMatch[1]) {
      filename = filenameMatch[1].replace(/['"]/g, '');
    }
  }

  downloadFile(blob, filename, mimeType);
}

type Report = { id: string; name: string; description?: string };
type HtmlResponse = { html: string; pagination?: PaginationData };
type PaginationData = {
  currentPage: number;
  pageSize: number;
  totalPages: number;
  totalRecords: number;
};

// Fixed page size for all report generation
const PAGE_SIZE = 20;
type ReportRunStatus = 'queued' | 'running' | 'succeeded' | 'failed';
type ReportRunMetadata = {
  runId: string;
  reportId: string;
  status: ReportRunStatus;
  statusMessage?: string;
  downloadUrl?: string;
  resultUrl?: string;
  error?: { code: string; message: string };
};

// Type guard to check if result is ReportRunMetadata
function isReportRunMetadata(result: unknown): result is ReportRunMetadata {
  return (
    typeof result === 'object' &&
    result !== null &&
    'runId' in result &&
    'status' in result
  );
}

// Get API base URL from environment variable
const REPORTING_API_BASE_URL =
  process.env.REACT_APP_REPORTING_API_BASE_URL ||
  'http://localhost:4000/api/v1';

const ReportingCrs: React.FC = () => {
  const user = useSelector(currentUser);
  const token = user?.reportingApiToken;

  // State
  const [selectedReport, setSelectedReport] = useState<Report | null>(null);
  const [hasGenerated, setHasGenerated] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [exportLoading, setExportLoading] = useState<boolean>(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});
  // Async execution state
  const [asyncRun, setAsyncRun] = useState<ReportRunMetadata | null>(null);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [asyncHtmlData, setAsyncHtmlData] = useState<HtmlResponse | null>(null);
  const [generationError, setGenerationError] = useState<string | null>(null);

  // Create SDK client configuration
  const clientConfig = useMemo(
    () => ({
      baseUrl: REPORTING_API_BASE_URL,
      authProvider: createTokenAuthProvider(token || '')
    }),
    [token]
  );

  // Create a client ref for direct API calls (for XLSX/PDF exports)
  const clientRef = useRef<ReturnType<typeof createReportingClient> | null>(
    null
  );
  if (!clientRef.current && token) {
    clientRef.current = createReportingClient(clientConfig);
  }

  // SDK hooks
  const {
    data: reports,
    loading: loadingReports,
    error: reportsError
  } = useReports(clientConfig);

  const {
    data: reportConfig,
    loading: loadingConfig,
    error: configError
  } = useReportConfig(clientConfig, selectedReport?.id || null);

  const filterState = useFilterState(reportConfig);

  const { getOptions, isLoading: isOptionsLoading } = useFilterOptions(
    clientConfig,
    selectedReport?.id || null,
    reportConfig?.filters || [],
    filterState.values
  );

  const generator = useGenerateReport(clientConfig, selectedReport?.id || null);

  // Reset generation data when report changes (but preserve layout state)
  useEffect(() => {
    generator.clear();
    setAsyncHtmlData(null);
    setGenerationError(null);
  }, [selectedReport?.id]);

  // Combine errors for display
  const error =
    reportsError ||
    configError ||
    generator.error ||
    (generationError ? { message: generationError } : null);

  // Handle report selection from dropdown
  const handleReportChange = useCallback(
    ({ selectedItem }: { selectedItem: Report | null }) => {
      setSelectedReport(selectedItem);
      setCurrentPage(1);
      setExportError(null);
      setGenerationError(null);
      // Don't reset hasGenerated - keep the layout state to avoid jarring switch
    },
    []
  );

  // Handle filter value change
  const handleFilterChange = useCallback(
    (filterId: string, value: unknown) => {
      filterState.setValue(filterId, value);
      // Clear validation error for this filter when value changes
      setValidationErrors((prev) => {
        if (prev[filterId]) {
          const newErrors = { ...prev };
          delete newErrors[filterId];
          return newErrors;
        }
        return prev;
      });
    },
    [filterState]
  );

  // Helper to fetch result from resultUrl after async completion
  const fetchAsyncResult = useCallback(
    async (resultUrl: string): Promise<HtmlResponse> => {
      const reqHeaders: Record<string, string> = {
        'Content-Type': 'application/json'
      };
      if (token) {
        reqHeaders['Authorization'] = `Bearer ${token}`;
      }
      const absoluteUrl = resolveApiUrl(REPORTING_API_BASE_URL, resultUrl);
      const response = await fetch(absoluteUrl, { headers: reqHeaders });
      if (!response.ok) {
        throw new Error(`Failed to fetch result: ${response.statusText}`);
      }

      // Extract pagination info from response headers
      const pagination: PaginationData = {
        currentPage: parseInt(
          response.headers.get('x-report-current-page') || '1',
          10
        ),
        pageSize: parseInt(
          response.headers.get('x-report-page-size') || String(PAGE_SIZE),
          10
        ),
        totalPages: parseInt(
          response.headers.get('x-report-total-pages') || '1',
          10
        ),
        totalRecords: parseInt(
          response.headers.get('x-report-total-records') || '0',
          10
        )
      };

      const data = await response.json();
      return {
        ...data,
        pagination
      };
    },
    [token]
  );

  // Handle generate report (HTML format) - uses async execution mode
  const handleGenerateReport = useCallback(async () => {
    if (!clientRef.current || !selectedReport) return;

    try {
      setValidationErrors({});
      setGenerationError(null);
      setIsGenerating(true);
      setAsyncRun(null);
      setAsyncHtmlData(null);
      generator.clear();
      filterState.validate();
      const filters = filterState.getSubmitValues();

      // Step 1: Start async generation
      const run = await clientRef.current.generateReportAsync(
        selectedReport.id,
        {
          filters,
          format: 'html',
          page: currentPage,
          pageSize: PAGE_SIZE
        }
      );

      setAsyncRun(run);

      // Step 2: Poll until complete
      const runResult = await clientRef.current.pollReportRunUntilComplete(
        run.runId,
        {
          pollIntervalMs: 2000,
          maxWaitMs: 600000, // 10 minutes
          onStatusChange: (updatedRun: ReportRunMetadata) =>
            setAsyncRun(updatedRun)
        }
      );

      // Check if the result is run metadata (async mode)
      if (isReportRunMetadata(runResult)) {
        // SDK checks for 'completed' or 'failed'
        if (runResult.status === 'failed') {
          throw new Error(
            runResult.error?.message || 'Report generation failed'
          );
        }

        // Fetch the HTML result using resultUrl
        if (runResult.resultUrl) {
          const htmlResult = await fetchAsyncResult(runResult.resultUrl);
          setAsyncHtmlData(htmlResult);
        }
      } else if ('html' in runResult) {
        // Direct HtmlResponse returned
        setAsyncHtmlData(runResult as HtmlResponse);
      }

      setHasGenerated(true);
    } catch (err) {
      // Check if it's a validation error with field-specific errors
      const errObj = Object(err);
      if (errObj.errors && typeof errObj.errors === 'object') {
        setValidationErrors(errObj.errors);
      }
      // Extract error message for display
      // Handle API error response format with detailed errors
      let errorMessage = 'An error occurred while generating the report';

      // Check if there are detailed errors in the response
      if (
        errObj.details &&
        Array.isArray(errObj.details.errors) &&
        errObj.details.errors.length > 0
      ) {
        // Use the first detailed error message
        errorMessage = errObj.details.errors[0].message;
      } else if (errObj.message) {
        errorMessage = errObj.message;

        // Transform "Missing required filter values" error to use filter names instead of IDs
        if (
          errorMessage.startsWith('Missing required filter values:') &&
          reportConfig?.filters
        ) {
          const filterIdsMatch = errorMessage.match(
            /Missing required filter values: (.+)/
          );
          if (filterIdsMatch) {
            const filterIds = filterIdsMatch[1].split(', ');
            const filterNames = filterIds.map((id) => {
              const filter = reportConfig.filters.find((f) => f.id === id);
              return filter ? filter.name : id;
            });
            errorMessage = `Missing required filter values: ${filterNames.join(
              ', '
            )}`;
          }
        }
      } else if (err instanceof Error) {
        errorMessage = err.message;
      }
      setGenerationError(errorMessage);
    } finally {
      setIsGenerating(false);
      setAsyncRun(null);
    }
  }, [filterState, generator, currentPage, selectedReport, fetchAsyncResult]);

  // Handle CSV export - uses async execution mode
  const handleExportCsv = useCallback(async () => {
    if (!clientRef.current || !selectedReport) return;
    try {
      setExportError(null);
      setExportLoading(true);
      setAsyncRun(null);
      filterState.validate();
      const filters = filterState.getSubmitValues();

      // Step 1: Start async generation
      const run = await clientRef.current.generateReportAsync(
        selectedReport.id,
        {
          filters,
          format: 'csv',
          pageSize: 10000
        }
      );

      setAsyncRun(run);

      // Step 2: Poll until complete
      const runResult = await clientRef.current.pollReportRunUntilComplete(
        run.runId,
        {
          pollIntervalMs: 2000,
          maxWaitMs: 600000,
          onStatusChange: (updatedRun: ReportRunMetadata) =>
            setAsyncRun(updatedRun)
        }
      );

      if (isReportRunMetadata(runResult)) {
        if (runResult.status === 'failed') {
          throw new Error(runResult.error?.message || 'CSV export failed');
        }

        if (runResult.downloadUrl && token) {
          const absoluteUrl = resolveApiUrl(
            REPORTING_API_BASE_URL,
            runResult.downloadUrl
          );
          const filename = `${selectedReport.name || 'report'}.csv`;
          await downloadWithAuth(absoluteUrl, token, filename, 'text/csv');
        }
      }
    } catch (err) {
      setExportError(
        err instanceof Error ? err.message : 'Failed to export CSV'
      );
    } finally {
      setExportLoading(false);
      setAsyncRun(null);
    }
  }, [filterState, selectedReport]);

  // Handle XLSX export - uses async execution mode
  const handleExportXlsx = useCallback(async () => {
    if (!clientRef.current || !selectedReport) return;
    try {
      setExportError(null);
      setExportLoading(true);
      setAsyncRun(null);
      filterState.validate();
      const filters = filterState.getSubmitValues();

      // Step 1: Start async generation
      const run = await clientRef.current.generateReportAsync(
        selectedReport.id,
        {
          filters,
          format: 'xlsx',
          pageSize: 10000
        }
      );

      setAsyncRun(run);

      // Step 2: Poll until complete
      const runResult = await clientRef.current.pollReportRunUntilComplete(
        run.runId,
        {
          pollIntervalMs: 2000,
          maxWaitMs: 600000,
          onStatusChange: (updatedRun: ReportRunMetadata) =>
            setAsyncRun(updatedRun)
        }
      );

      if (isReportRunMetadata(runResult)) {
        if (runResult.status === 'failed') {
          throw new Error(runResult.error?.message || 'Excel export failed');
        }

        if (runResult.downloadUrl && token) {
          const absoluteUrl = resolveApiUrl(
            REPORTING_API_BASE_URL,
            runResult.downloadUrl
          );
          const filename = `${selectedReport.name || 'report'}.xlsx`;
          await downloadWithAuth(
            absoluteUrl,
            token,
            filename,
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          );
        }
      }
    } catch (err) {
      setExportError(
        err instanceof Error ? err.message : 'Failed to export Excel'
      );
    } finally {
      setExportLoading(false);
      setAsyncRun(null);
    }
  }, [filterState, selectedReport]);

  // Handle PDF export - uses async execution mode
  const handleExportPdf = useCallback(async () => {
    if (!clientRef.current || !selectedReport) return;
    try {
      setExportError(null);
      setExportLoading(true);
      setAsyncRun(null);
      filterState.validate();
      const filters = filterState.getSubmitValues();

      // Step 1: Start async generation
      const run = await clientRef.current.generateReportAsync(
        selectedReport.id,
        {
          filters,
          format: 'pdf',
          pageSize: 10000
        }
      );

      setAsyncRun(run);

      // Step 2: Poll until complete
      const runResult = await clientRef.current.pollReportRunUntilComplete(
        run.runId,
        {
          pollIntervalMs: 2000,
          maxWaitMs: 600000,
          onStatusChange: (updatedRun: ReportRunMetadata) =>
            setAsyncRun(updatedRun)
        }
      );

      if (isReportRunMetadata(runResult)) {
        if (runResult.status === 'failed') {
          throw new Error(runResult.error?.message || 'PDF export failed');
        }

        if (runResult.downloadUrl && token) {
          const absoluteUrl = resolveApiUrl(
            REPORTING_API_BASE_URL,
            runResult.downloadUrl
          );
          const filename = `${selectedReport.name || 'report'}.pdf`;
          await downloadWithAuth(
            absoluteUrl,
            token,
            filename,
            'application/pdf'
          );
        }
      }
    } catch (err) {
      setExportError(
        err instanceof Error ? err.message : 'Failed to export PDF'
      );
    } finally {
      setExportLoading(false);
      setAsyncRun(null);
    }
  }, [filterState, selectedReport]);

  // Handle export from dropdown selection
  const handleExportSelect = useCallback(
    ({
      selectedItem
    }: {
      selectedItem: { id: string; text: string } | null;
    }) => {
      if (!selectedItem) return;
      switch (selectedItem.id) {
        case 'csv':
          handleExportCsv();
          break;
        case 'xlsx':
          handleExportXlsx();
          break;
        case 'pdf':
          handleExportPdf();
          break;
      }
    },
    [handleExportCsv, handleExportXlsx, handleExportPdf]
  );

  // Handle pagination - uses async execution mode
  const handlePageChange = useCallback(
    async (page: number) => {
      if (!clientRef.current || !selectedReport) return;
      setCurrentPage(page);
      setIsGenerating(true);
      setAsyncRun(null);

      try {
        const filters = filterState.getSubmitValues();

        // Step 1: Start async generation
        const run = await clientRef.current.generateReportAsync(
          selectedReport.id,
          {
            filters,
            format: 'html',
            page,
            pageSize: PAGE_SIZE
          }
        );

        setAsyncRun(run);

        // Step 2: Poll until complete
        const runResult = await clientRef.current.pollReportRunUntilComplete(
          run.runId,
          {
            pollIntervalMs: 2000,
            maxWaitMs: 600000,
            onStatusChange: (updatedRun: ReportRunMetadata) =>
              setAsyncRun(updatedRun)
          }
        );

        if (isReportRunMetadata(runResult)) {
          if (runResult.status === 'failed') {
            throw new Error(
              runResult.error?.message || 'Report generation failed'
            );
          }

          // Fetch the HTML result using resultUrl
          if (runResult.resultUrl) {
            const htmlResult = await fetchAsyncResult(runResult.resultUrl);
            setAsyncHtmlData(htmlResult);
          }
        } else if ('html' in runResult) {
          setAsyncHtmlData(runResult as HtmlResponse);
        }
      } finally {
        setIsGenerating(false);
        setAsyncRun(null);
      }
    },
    [filterState, selectedReport, fetchAsyncResult]
  );

  // Get visible filters with dynamic options
  const visibleFilters = useMemo(() => {
    if (!reportConfig?.filters) return [];
    return reportConfig.filters
      .filter((f) => !f.hidden && f.source !== 'system')
      .map((filter) => ({
        ...filter,
        options:
          getOptions(filter.id).length > 0
            ? getOptions(filter.id)
            : filter.options
      }));
  }, [reportConfig, getOptions]);

  // Get HTML data and pagination - prefer async result, fallback to sync
  const htmlData: HtmlResponse | null = asyncHtmlData || generator.htmlData;
  // Always provide a pagination object when we have data, with sensible defaults
  const pagination: PaginationData = htmlData?.pagination || {
    currentPage: currentPage,
    pageSize: PAGE_SIZE,
    totalPages: 1,
    totalRecords: 0
  };

  // Render loading skeleton for filters
  const renderFilterSkeleton = () => (
    <div className="reporting-new__skeleton">
      <div className="reporting-new__skeleton-item" />
      <div className="reporting-new__skeleton-item" />
      <div className="reporting-new__skeleton-item" />
    </div>
  );

  // Render validation error if exists
  const renderFilterError = (filterId: string) => {
    const errorMsg = validationErrors[filterId];
    if (!errorMsg) return null;
    return <span className="reporting-new__filter-error">{errorMsg}</span>;
  };

  // Render a single filter control
  const renderFilterControl = (filter: {
    id: string;
    name: string;
    type: string;
    required?: boolean;
    options?: Array<{ label: string; value: string }>;
  }) => {
    const isLoading = isOptionsLoading(filter.id);
    const filterOptions = filter.options || [];
    const hasError = !!validationErrors[filter.id];

    switch (filter.type) {
      case 'date': {
        // Convert stored YYYY-MM-DD string to Date object for the DatePicker
        const storedDateValue = filterState.values[filter.id];
        const dateValue = storedDateValue
          ? parseDateString(String(storedDateValue))
          : null;

        return (
          <div
            key={filter.id}
            className={`reporting-new__filter-item ${
              hasError ? 'reporting-new__filter-item--error' : ''
            }`}
          >
            <DatePicker
              datePickerType="single"
              dateFormat="d/m/Y"
              value={dateValue ? [dateValue] : []}
              onChange={(dates: Date[]) => {
                const formattedDate = dates[0]
                  ? formatDateForApi(dates[0])
                  : null;
                handleFilterChange(filter.id, formattedDate);
              }}
            >
              <DatePickerInput
                id={filter.id}
                labelText={filter.name}
                placeholder="dd/mm/yyyy"
                size="sm"
                invalid={hasError}
              />
            </DatePicker>
            {renderFilterError(filter.id)}
          </div>
        );
      }

      case 'select':
        return (
          <div
            key={filter.id}
            className={`reporting-new__filter-item ${
              hasError ? 'reporting-new__filter-item--error' : ''
            }`}
          >
            {isLoading ? (
              <Loading small withOverlay={false} />
            ) : (
              <Dropdown
                id={filter.id}
                titleText={filter.name}
                label="Select..."
                size="sm"
                items={filterOptions}
                itemToString={(item: { label: string; value: string } | null) =>
                  item ? item.label : ''
                }
                selectedItem={
                  filterOptions.find(
                    (opt) => opt.value === filterState.values[filter.id]
                  ) || null
                }
                onChange={({
                  selectedItem
                }: {
                  selectedItem: { label: string; value: string } | null;
                }) => {
                  handleFilterChange(filter.id, selectedItem?.value || null);
                }}
                invalid={hasError}
                invalidText={validationErrors[filter.id]}
              />
            )}
          </div>
        );

      case 'multiSelect': {
        const rawValues = filterState.values[filter.id];
        const selectedValues = Array.isArray(rawValues) ? rawValues : [];
        const selectedItems = filterOptions.filter((opt) =>
          selectedValues.includes(String(opt.value))
        );
        return (
          <div
            key={filter.id}
            className={`reporting-new__filter-item ${
              hasError ? 'reporting-new__filter-item--error' : ''
            }`}
          >
            {isLoading ? (
              <Loading small withOverlay={false} />
            ) : (
              <MultiSelect
                key={`${filter.id}-${selectedValues.join(',')}`}
                id={filter.id}
                titleText={filter.name}
                label="Select..."
                size="sm"
                items={filterOptions}
                itemToString={(item: { label: string; value: string } | null) =>
                  item ? item.label : ''
                }
                initialSelectedItems={selectedItems}
                onChange={({
                  selectedItems: items
                }: {
                  selectedItems: Array<{ label: string; value: string }>;
                }) => {
                  handleFilterChange(
                    filter.id,
                    items.map((i) => i.value)
                  );
                }}
                invalid={hasError}
                invalidText={validationErrors[filter.id]}
              />
            )}
          </div>
        );
      }

      case 'text':
        return (
          <div
            key={filter.id}
            className={`reporting-new__filter-item ${
              hasError ? 'reporting-new__filter-item--error' : ''
            }`}
          >
            <TextInput
              id={filter.id}
              labelText={filter.name}
              placeholder="Enter value..."
              size="sm"
              value={String(filterState.values[filter.id] || '')}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                handleFilterChange(filter.id, e.target.value);
              }}
              invalid={hasError}
              invalidText={validationErrors[filter.id]}
            />
          </div>
        );

      case 'number':
        return (
          <div
            key={filter.id}
            className={`reporting-new__filter-item ${
              hasError ? 'reporting-new__filter-item--error' : ''
            }`}
          >
            <NumberInput
              id={filter.id}
              label={filter.name}
              size="sm"
              value={Number(filterState.values[filter.id]) || 0}
              onChange={(
                _evt: React.SyntheticEvent,
                { value }: { value: string | number }
              ) => {
                handleFilterChange(
                  filter.id,
                  typeof value === 'string' ? parseFloat(value) : value
                );
              }}
              hideSteppers={false}
              invalid={hasError}
              invalidText={validationErrors[filter.id]}
            />
          </div>
        );

      default:
        return null;
    }
  };

  // Check if generate button should be enabled
  const canGenerate =
    selectedReport && !generator.loading && !loadingConfig && !isGenerating;

  return (
    <div id="ReportingCrs" data-testid="ReportingCrs" className="reporting-crs">
      <FullHeaderLeftSideFullPanelLayout enableHeaderGlobalBar={true}>
        <div className="reporting-new__wrapper">
          {/* Error notification */}
          {(error || exportError) && (
            <InlineNotification
              kind="error"
              title="Error"
              subtitle={exportError || error?.message || 'An error occurred'}
              onCloseButtonClick={() => {
                setExportError(null);
                setGenerationError(null);
                generator.clear();
              }}
              className="reporting-new__error"
            />
          )}

          {!token && (
            <InlineNotification
              kind="warning"
              title="Authentication Required"
              subtitle="No reporting API token found. Please ensure you are logged in."
              hideCloseButton
              className="reporting-new__error"
            />
          )}

          {/* Before Report Generated - Two Panel Layout */}
          {!hasGenerated && (
            <div className="reporting-new__initial-layout">
              {/* Left Sidebar - Filters */}
              <div className="reporting-new__sidebar">
                {/* Report Selector */}
                <div className="reporting-new__sidebar-section">
                  {loadingReports ? (
                    <Loading
                      small
                      withOverlay={false}
                      description="Loading reports..."
                    />
                  ) : (
                    <Dropdown
                      id="report-selector"
                      titleText="Report"
                      label="Select a report"
                      items={reports || []}
                      itemToString={(item: Report | null) =>
                        item ? item.name : ''
                      }
                      selectedItem={selectedReport}
                      onChange={handleReportChange}
                    />
                  )}
                </div>

                {/* Filter Controls */}
                {selectedReport && (
                  <div className="reporting-new__sidebar-filters">
                    {loadingConfig
                      ? renderFilterSkeleton()
                      : visibleFilters.map((filter) =>
                          renderFilterControl(filter)
                        )}
                  </div>
                )}

                {/* Generate Button */}
                <div className="reporting-new__sidebar-actions">
                  <Button
                    kind="primary"
                    onClick={handleGenerateReport}
                    disabled={!canGenerate}
                    className="reporting-new__generate-btn"
                  >
                    Generate Report
                  </Button>
                </div>
              </div>

              {/* Right Panel - Placeholder */}
              <div className="reporting-new__placeholder">
                <div className="reporting-new__placeholder-content">
                  <div className="reporting-new__placeholder-icon">
                    <img
                      src="/shapes-lines-small.svg"
                      alt="Report placeholder"
                      className="reporting-new__placeholder-graphic"
                    />
                  </div>
                  <p className="reporting-new__placeholder-text">
                    Your report will appear here
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* After Report Generated - Horizontal Filter Bar + Report */}
          {hasGenerated && selectedReport && (
            <div className="reporting-new__generated-layout">
              {/* Top Filter Bar */}
              <div className="reporting-new__filter-bar">
                <div className="reporting-new__filter-bar-left">
                  {/* Report Selector */}
                  <div className="reporting-new__filter-item reporting-new__filter-item--report">
                    <Dropdown
                      id="report-selector-bar"
                      titleText="Report"
                      label="Select a report"
                      size="sm"
                      items={reports || []}
                      itemToString={(item: Report | null) =>
                        item ? item.name : ''
                      }
                      selectedItem={selectedReport}
                      onChange={handleReportChange}
                    />
                  </div>

                  {/* Filter Controls */}
                  {visibleFilters.map((filter) => renderFilterControl(filter))}
                </div>

                <div className="reporting-new__filter-bar-right">
                  <Button
                    kind="primary"
                    size="sm"
                    onClick={handleGenerateReport}
                    disabled={!canGenerate}
                  >
                    Generate Report
                  </Button>
                </div>
              </div>

              {/* Report Header */}
              <div className="reporting-new__report-header">
                <div className="reporting-new__report-title">
                  <span>{selectedReport.name}</span>
                  <span className="reporting-new__report-timestamp">
                    (Generated:{' '}
                    {dayjs().utc().tz().format('ddd, DD MMM YYYY h:mm A')})
                  </span>
                </div>

                <div className="reporting-new__report-controls">
                  {/* Pagination - always visible when report is generated */}
                  {htmlData && (
                    <div className="reporting-new__pagination">
                      <span className="reporting-new__page-label">Page</span>
                      <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={
                          currentPage <= 1 || generator.loading || isGenerating
                        }
                        className="reporting-new__page-btn"
                        aria-label="Previous page"
                      >
                        &lt;
                      </button>
                      <Dropdown
                        id="page-selector"
                        titleText=""
                        label={String(currentPage)}
                        size="sm"
                        items={Array.from(
                          { length: pagination.totalPages },
                          (_, i) => ({
                            id: String(i + 1),
                            text: String(i + 1)
                          })
                        )}
                        itemToString={(
                          item: { id: string; text: string } | null
                        ) => (item ? item.text : '')}
                        selectedItem={{
                          id: String(currentPage),
                          text: String(currentPage)
                        }}
                        onChange={({
                          selectedItem
                        }: {
                          selectedItem: { id: string; text: string } | null;
                        }) => {
                          if (selectedItem) {
                            const page = parseInt(selectedItem.id, 10);
                            if (page !== currentPage) {
                              handlePageChange(page);
                            }
                          }
                        }}
                        disabled={generator.loading || isGenerating}
                        className="reporting-new__page-selector"
                      />
                      <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={
                          currentPage >= pagination.totalPages ||
                          generator.loading ||
                          isGenerating
                        }
                        className="reporting-new__page-btn"
                        aria-label="Next page"
                      >
                        &gt;
                      </button>
                    </div>
                  )}

                  {/* Download Dropdown */}
                  <Dropdown
                    id="export-dropdown"
                    titleText=""
                    label={exportLoading ? 'Exporting...' : 'Download'}
                    size="sm"
                    items={[
                      { id: 'csv', text: 'CSV' },
                      { id: 'xlsx', text: 'Excel (XLSX)' },
                      { id: 'pdf', text: 'PDF' }
                    ]}
                    itemToString={(item: { id: string; text: string } | null) =>
                      item ? item.text : ''
                    }
                    selectedItem={null}
                    onChange={handleExportSelect}
                    disabled={
                      exportLoading || generator.loading || isGenerating
                    }
                    className="reporting-new__download-dropdown"
                  />
                </div>
              </div>

              {/* Progress Bar (decorative) */}
              <div className="reporting-new__progress-bar" />

              {/* Report Content */}
              <div className="reporting-new__report-content">
                {(generator.loading || isGenerating) && (
                  <Loading
                    description={
                      asyncRun?.statusMessage ||
                      (asyncRun?.status === 'queued'
                        ? 'Queued...'
                        : asyncRun?.status === 'running'
                        ? 'Generating report...'
                        : 'Generating report...')
                    }
                    withOverlay={true}
                  />
                )}

                {htmlData && (
                  <iframe
                    className="reporting-new__html-content"
                    srcDoc={htmlData.html}
                    title="Report Preview"
                    sandbox="allow-same-origin"
                  />
                )}

                {htmlData &&
                  htmlData.pagination &&
                  htmlData.pagination.totalRecords === 0 && (
                    <div className="reporting-new__no-data">
                      <p>No data found for the selected filters.</p>
                    </div>
                  )}
              </div>
            </div>
          )}
        </div>
      </FullHeaderLeftSideFullPanelLayout>
    </div>
  );
};

export default React.memo(ReportingCrs);
